import React, { useState } from 'react';
import { api } from '../utils/api';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { User, Lock, Mail, Building2, Shield, FileText } from 'lucide-react';
import { TermsViewerDialog } from '../components/TermsViewerDialog';

export default function ProfilePage() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [passwordData, setPasswordData] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });

  const handleChangePassword = async (e) => {
    e.preventDefault();
    
    if (passwordData.new_password !== passwordData.confirm_password) {
      toast.error('Nova senha e confirmação não coincidem');
      return;
    }

    if (passwordData.new_password.length < 6) {
      toast.error('Nova senha deve ter no mínimo 6 caracteres');
      return;
    }

    setLoading(true);
    try {
      await api.put('/auth/change-password', {
        current_password: passwordData.current_password,
        new_password: passwordData.new_password
      });
      
      toast.success('Senha alterada com sucesso!');
      setPasswordData({
        current_password: '',
        new_password: '',
        confirm_password: ''
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao alterar senha');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="profile-page">
      <div className="mb-8">
        <h1 className="text-4xl font-bold text-slate-900 mb-2 tracking-tight">Meu Perfil</h1>
        <p className="text-slate-600">Gerencie suas informações pessoais e credenciais</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Informações do Usuário */}
        <div className="lg:col-span-1">
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
            <div className="flex flex-col items-center text-center mb-6">
              <div className="h-24 w-24 rounded-full bg-teal-100 flex items-center justify-center mb-4">
                <User className="h-12 w-12 text-teal-700" strokeWidth={2} />
              </div>
              <h2 className="text-xl font-bold text-slate-900">{user?.name}</h2>
              <p className="text-sm text-slate-500">{user?.email}</p>
            </div>

            <div className="space-y-4 border-t border-slate-200 pt-4">
              <div className="flex items-center gap-3 text-sm">
                <Shield className="h-5 w-5 text-slate-400" />
                <div>
                  <p className="text-slate-500">Nível de Acesso</p>
                  <p className="font-medium text-slate-900">
                    {user?.role === 'admin' ? 'Administrador' : 'Marcador'}
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-3 text-sm">
                <Mail className="h-5 w-5 text-slate-400" />
                <div>
                  <p className="text-slate-500">Email</p>
                  <p className="font-medium text-slate-900 break-all">{user?.email}</p>
                </div>
              </div>

              {user?.unit_id && (
                <div className="flex items-center gap-3 text-sm">
                  <Building2 className="h-5 w-5 text-slate-400" />
                  <div>
                    <p className="text-slate-500">Unidade</p>
                    <p className="font-medium text-slate-900">Vinculado</p>
                  </div>
                </div>
              )}
            </div>

            <div className="border-t border-slate-200 pt-4 mt-4">
              <TermsViewerDialog
                trigger={
                  <button
                    type="button"
                    className="flex items-center gap-3 text-sm text-teal-700 hover:text-teal-800 hover:underline font-medium w-full"
                    data-testid="profile-view-terms-link"
                  >
                    <FileText className="h-5 w-5 text-slate-400" />
                    Ver Termos de Uso e Política de Privacidade
                  </button>
                }
              />
            </div>
          </div>
        </div>

        {/* Alterar Senha */}
        <div className="lg:col-span-2">
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="p-3 rounded-lg bg-teal-50">
                <Lock className="h-6 w-6 text-teal-600" strokeWidth={2} />
              </div>
              <div>
                <h2 className="text-xl font-bold text-slate-900">Alterar Senha</h2>
                <p className="text-sm text-slate-600">Mantenha sua conta segura com uma senha forte</p>
              </div>
            </div>

            <form onSubmit={handleChangePassword} className="space-y-6">
              <div>
                <Label htmlFor="current_password">Senha Atual *</Label>
                <Input
                  id="current_password"
                  type="password"
                  value={passwordData.current_password}
                  onChange={(e) => setPasswordData({ ...passwordData, current_password: e.target.value })}
                  placeholder="Digite sua senha atual"
                  required
                  className="h-11 rounded-lg mt-2"
                  data-testid="current-password-input"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <Label htmlFor="new_password">Nova Senha *</Label>
                  <Input
                    id="new_password"
                    type="password"
                    value={passwordData.new_password}
                    onChange={(e) => setPasswordData({ ...passwordData, new_password: e.target.value })}
                    placeholder="Mínimo 6 caracteres"
                    required
                    minLength={6}
                    className="h-11 rounded-lg mt-2"
                    data-testid="new-password-input"
                  />
                </div>

                <div>
                  <Label htmlFor="confirm_password">Confirmar Nova Senha *</Label>
                  <Input
                    id="confirm_password"
                    type="password"
                    value={passwordData.confirm_password}
                    onChange={(e) => setPasswordData({ ...passwordData, confirm_password: e.target.value })}
                    placeholder="Digite novamente"
                    required
                    className="h-11 rounded-lg mt-2"
                    data-testid="confirm-password-input"
                  />
                </div>
              </div>

              <div className="bg-slate-50 border border-slate-200 rounded-lg p-4">
                <p className="text-xs text-slate-600 font-medium mb-2">Requisitos da senha:</p>
                <ul className="text-xs text-slate-500 space-y-1">
                  <li className="flex items-center gap-2">
                    <span className="text-teal-600">✓</span>
                    Mínimo de 6 caracteres
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="text-teal-600">✓</span>
                    Recomendado: Use letras, números e caracteres especiais
                  </li>
                </ul>
              </div>

              <div className="flex gap-3 pt-4 border-t">
                <Button
                  type="submit"
                  disabled={loading}
                  className="bg-teal-700 hover:bg-teal-800 text-white rounded-lg h-11 px-6 font-medium shadow-sm"
                  data-testid="change-password-button"
                >
                  {loading ? (
                    <div className="flex items-center gap-2">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      Alterando...
                    </div>
                  ) : (
                    'Alterar Senha'
                  )}
                </Button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}
